import { api } from './api';

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'refunded';
export type PaymentMethod = 'cash' | 'check' | 'bank_transfer' | 'credit_card' | 'paypal' | 'venmo' | 'zelle' | 'stripe' | 'other';
export type CancellationReason = 'client_requested' | 'duplicate' | 'incorrect_amount' | 'service_not_delivered' | 'payment_arrangement' | 'other';

export interface InvoiceLineItem {
  _id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  serviceId?: string;
  bookingId?: string;
}

export interface PaymentRecord {
  _id: string;
  amount: number;
  method: PaymentMethod;
  paidAt: string;
  reference?: string;
  notes?: string;
  recordedBy: {
    _id: string;
    profile: {
      firstName: string;
      lastName: string;
    };
  };
  createdAt: string;
}

export interface Invoice {
  _id: string;
  invoiceNumber: string;

  clientId: {
    _id: string;
    firstName: string;
    lastName: string;
    avatarColor?: string;
  };
  clientName: string;
  clientEmail: string;
  clientAddress?: string;

  lineItems: InvoiceLineItem[];

  subtotal: number;
  taxRate: number;
  taxAmount: number;
  discountType: 'fixed' | 'percentage' | null;
  discountValue: number;
  discountAmount: number;
  total: number;
  amountPaid: number;
  amountDue: number;

  currency: string;

  issueDate: string;
  dueDate: string;
  paidDate?: string;

  status: InvoiceStatus;

  payments: PaymentRecord[];
  bookingIds: string[];

  notes?: string;
  internalNotes?: string;
  terms?: string;

  sentAt?: string;
  sentTo?: string;

  cancellationReason?: CancellationReason;
  cancellationNote?: string;
  cancelledAt?: string;
  cancelledBy?: {
    _id: string;
    profile: {
      firstName: string;
      lastName: string;
    };
  };

  formattedTotal: string;
  formattedAmountDue: string;
  isOverdue: boolean;
  daysUntilDue: number;

  createdAt: string;
  updatedAt: string;
}

export interface InvoiceStats {
  total: number;
  draft: number;
  sent: number;
  paid: number;
  overdue: number;
  cancelled: number;
  totalRevenue: number;
  totalOutstanding: number;
}

export interface InvoicesListParams {
  page?: number;
  limit?: number;
  status?: InvoiceStatus | 'overdue' | 'all';
  clientId?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: 'invoiceNumber' | 'issueDate' | 'dueDate' | 'total' | 'status' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

export interface InvoicesListResponse {
  invoices: Invoice[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface CreateLineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  serviceId?: string;
  bookingId?: string;
}

export interface CreateInvoiceData {
  clientId: string;
  lineItems: CreateLineItem[];
  issueDate?: string;
  dueDate: string;
  taxRate?: number;
  discountType?: 'fixed' | 'percentage' | null;
  discountValue?: number;
  notes?: string;
  internalNotes?: string;
  terms?: string;
  bookingIds?: string[];
  status?: 'draft' | 'sent';
}

export interface UpdateInvoiceData {
  lineItems?: CreateLineItem[];
  dueDate?: string;
  taxRate?: number;
  discountType?: 'fixed' | 'percentage' | null;
  discountValue?: number;
  notes?: string;
  internalNotes?: string;
  terms?: string;
}

export interface RecordPaymentData {
  amount: number;
  method: PaymentMethod;
  paidAt: string;
  reference?: string;
  notes?: string;
}

// API Functions
export const invoicesApi = {
  // List invoices
  list: async (params: InvoicesListParams = {}): Promise<InvoicesListResponse> => {
    const response = await api.get('/invoices', { params });
    return response.data.data;
  },

  // Get single invoice
  get: async (id: string): Promise<Invoice> => {
    const response = await api.get(`/invoices/${id}`);
    return response.data.data.invoice;
  },

  // Get invoice stats
  getStats: async (): Promise<InvoiceStats> => {
    const response = await api.get('/invoices/stats');
    return response.data.data;
  },

  // Get next invoice number
  getNextNumber: async (): Promise<string> => {
    const response = await api.get('/invoices/next-number');
    return response.data.data.invoiceNumber;
  },

  // Create invoice
  create: async (data: CreateInvoiceData): Promise<Invoice> => {
    const response = await api.post('/invoices', data);
    return response.data.data.invoice;
  },

  // Create invoice from bookings
  createFromBookings: async (bookingIds: string[], dueDate: string): Promise<Invoice> => {
    const response = await api.post('/invoices/from-booking', { bookingIds, dueDate });
    return response.data.data.invoice;
  },

  // Update invoice
  update: async (id: string, data: UpdateInvoiceData): Promise<Invoice> => {
    const response = await api.patch(`/invoices/${id}`, data);
    return response.data.data.invoice;
  },

  // Send invoice
  send: async (id: string): Promise<{ invoice: Invoice; emailSent: boolean; emailSentTo: string; emailError?: string }> => {
    const response = await api.patch(`/invoices/${id}/send`);
    return response.data.data;
  },

  // Resend invoice email
  resend: async (id: string, email?: string): Promise<{ emailSent: boolean; emailSentTo: string; reminderCount: number }> => {
    const response = await api.post(`/invoices/${id}/resend`, { email });
    return response.data.data;
  },

  // Record payment
  recordPayment: async (id: string, data: RecordPaymentData): Promise<Invoice> => {
    const response = await api.post(`/invoices/${id}/payments`, data);
    return response.data.data.invoice;
  },

  // Cancel invoice
  cancel: async (id: string, reason: CancellationReason, note?: string): Promise<Invoice> => {
    const response = await api.patch(`/invoices/${id}/cancel`, { reason, note });
    return response.data.data.invoice;
  },

  // Extend due date
  extendDueDate: async (id: string, dueDate: string, reason?: string): Promise<Invoice> => {
    const response = await api.patch(`/invoices/${id}/due-date`, { dueDate, reason });
    return response.data.data.invoice;
  },

  // Delete invoice (draft only)
  delete: async (id: string): Promise<void> => {
    await api.delete(`/invoices/${id}`);
  },
};
